import Vue from 'vue';
import { ActionContext, Module } from 'vuex';
import { convertTimeStringToLocalTZ } from '@/helper/date-time';
import { StaffEntity } from '@/entities/staff.entity';

type CurrentStaff = StaffEntity;
export type StaffNotify = any;

interface CheeersStaffState {
  allStaffs: StaffEntity[];
  current: CurrentStaff;
  notify: StaffNotify[];
  selectedUser: StaffEntity;
}

type StaffActionContext = ActionContext<CheeersStaffState, CheeersRootState>;

const staffModule: Module<CheeersStaffState, CheeersRootState> = {
  namespaced: true,
  state: (): CheeersStaffState => ({
    current: undefined,
    notify: [],
    allStaffs: [],
    selectedUser: undefined
  }),
  getters: {
    allStaffs(state: CheeersStaffState) {
      return {
        ...state.allStaffs,
      };
    },
    getStaffById(state: CheeersStaffState) {
      return (staffId: number) =>
        state.allStaffs.filter((staff) => staff.id === staffId)[0];
    },
    currentStaff(state: CheeersStaffState) {
      if (!state.current) {
        return {};
      }
      const isCheckedIn =
        state.current.workingTimes &&
        state.current.workingTimes.length > 0 &&
        !!state.current.workingTimes[0].checkinTime;
      const isCheckedOut =
        state.current.workingTimes &&
        state.current.workingTimes.length > 0 &&
        !!state.current.workingTimes[0].checkoutTime;

      const checkInTime = isCheckedIn
        ? convertTimeStringToLocalTZ(state.current.workingTimes[0].checkinTime)
        : null;
      const checkOutTime = isCheckedOut
        ? convertTimeStringToLocalTZ(state.current.workingTimes[0].checkoutTime)
        : null;

      let isPause = false;
      if (
        isCheckedIn &&
        !isCheckedOut &&
        state.current.workingTimes[0].breakingTimes.length > 0
      ) {
        const { breakingTimes } = state.current.workingTimes[0];
        if (
          breakingTimes.length > 0 &&
          breakingTimes[breakingTimes.length - 1].from &&
          !breakingTimes[breakingTimes.length - 1].to
        ) {
          isPause = true;
        }
      }

      let { defaultGiftPoint } = state.current;
      let canUpdateDefaultGiftPoint = true;
      if (
        state?.current?.group?.company?.companyDefault &&
        state.current.group.company.defaultGiftPoint
      ) {
        canUpdateDefaultGiftPoint = false;
        defaultGiftPoint = state.current.group.company.defaultGiftPoint;
      }

      return {
        ...state.current,
        isCheckedIn,
        isCheckedOut,
        checkInTime,
        checkOutTime,
        isPause,
        defaultGiftPoint,
        canUpdateDefaultGiftPoint,
      };
    },
  },
  mutations: {
    updateLastLogin(state: CheeersStaffState) {
      state.current.lastLogin = new Date();
    },
    pushManyNotify(state: CheeersStaffState, notifyList: StaffNotify[]) {
      const oldIds = state.notify.map((item) => item.id);
      notifyList = notifyList.filter((item) => !oldIds.includes(item.id));

      state.notify = [...state.notify, ...notifyList];
    },
    setCurrentStaff(state: CheeersStaffState, staff: CurrentStaff) {
      state.current = {
        ...state.current,
        ...staff,
      };
    },
    removeFirstNotification(state: CheeersStaffState) {
      state.notify = state.notify.filter(
        (item: StaffNotify, index: number) => index !== 0,
      );
    },
    pushNotification(state: CheeersStaffState, notify: StaffNotify) {
      state.notify = [...state.notify, notify];
    },
    addStaff(state: CheeersStaffState, staff: StaffEntity) {
      state.allStaffs = [...state.allStaffs, staff];
    },
  },
  actions: {
    async fetchStaffById(
      { commit, state }: StaffActionContext,
      staffId: number,
    ) {
      const isStaffExist = state.allStaffs.some(
        (staff) => staff.id === staffId,
      );
      if (!isStaffExist) {
        const response = await Vue.prototype.$http.get(
          `/staff/${staffId}/show?join=position&join=group.company&join=staffTitleExecuteCount`,
        );
        commit('addStaff', response.data);
      }
    },
    async historyLogin() {
      try {
        await Vue.prototype.$http.post('/authentication/staff/history-login');
      } catch (error) {
        console.log(error);
      }
    },
    async fetchCurrentStaff({ commit }: StaffActionContext) {
      const response = await Vue.prototype.$http.get(
        `/authentication/staff?&timestamp=${new Date().getTime()}`,
      );
      commit('setCurrentStaff', response.data);
      if (response.data.unreadNotify && response.data.unreadNotify.length > 0) {
        response.data.unreadNotify.forEach((noti) => {
          commit('pushNotification', noti);
        });
      }
    },
    async getNotifyStaffByTheFirstAccessPage({ commit }: StaffActionContext) {
      const response = await Vue.prototype.$http.get(
        `/authentication/staff/get-notify-staff-by-the-first-access-page`,
      );
      response.data.forEach((noti) => {
        commit('pushNotification', noti);
      });
    },
    async check({ dispatch }: StaffActionContext, type: 'in' | 'out') {
      await Vue.prototype.$http.patch(`/authentication/staff/check${type}`);
      await dispatch('fetchCurrentStaff');
    },
    async overwork({ dispatch }: StaffActionContext) {
      await Vue.prototype.$http.patch('/authentication/staff/overwork');
      await dispatch('fetchCurrentStaff');
    },
    async pause({ dispatch }: StaffActionContext) {
      await Vue.prototype.$http.patch('/authentication/staff/pause');
      await dispatch('fetchCurrentStaff');
    },
    async continue({ dispatch }: StaffActionContext) {
      await Vue.prototype.$http.patch('/authentication/staff/continue');
      await dispatch('fetchCurrentStaff');
    },
    async removeFirstNotification({ commit }: StaffActionContext) {
      commit('removeFirstNotification');
    },
    async pushNotification(
      { commit }: StaffActionContext,
      payload: StaffNotify,
    ) {
      commit('pushNotification', payload);
    },
  },
};

export default staffModule;
